import { SUFFIX_OPTIONS, REG_EX, GENERAL } from 'utils/constants';

const SUFFIX_ARR = SUFFIX_OPTIONS.map((option) => option.value);

const piiFormModel = {
  nameModel: {
    groupId: 'name',
    formFields: {
      first: {
        name: 'first',
        label: 'First name',
        placeholder: 'Enter first name',
        min: 2,
        max: 40,
        matches: REG_EX.lettersSpaces,
        requiredErrMsg: 'First name is a required field.',
        minErrMsg: 'First name must be at least 2 characters.',
        maxErrMsg: 'First name must be 40 characters or less.',
        matchesErrMsg: 'First name can only contain letters and spaces.',
      },
      middle: {
        name: 'middle',
        label: 'Middle name',
        placeholder: 'Enter middle name',
        min: 1,
        max: 40,
        matches: REG_EX.lettersSpacesHyphensApostrophes,
        requiredErrMsg: 'Middle name is a required field.',
        minErrMsg: 'Middle name must be at least 1 character.',
        maxErrMsg: 'Middle name must be 40 characters or less.',
        matchesErrMsg:
          'Middle name can only contain letters, spaces, hyphens, and apostrophes.',
      },
      last: {
        name: 'last',
        label: 'Last name',
        placeholder: 'Enter last name',
        min: 2,
        max: 40,
        matches: REG_EX.lettersSpacesHyphensApostrophes,
        requiredErrMsg: 'Last name is a required field.',
        minErrMsg: 'Last name must be at least 2 characters.',
        maxErrMsg: 'Last name must be 40 characters or less.',
        matchesErrMsg:
          'Last name can only contain letters, spaces, hyphens, and apostrophes.',
      },
      suffix: {
        name: 'suffix',
        label: 'Suffix',
        placeholder: 'Select suffix',
        oneOf: SUFFIX_ARR,
        oneOfErrMsg: 'Please provide a valid suffix.',
      },
    },
  },
  addressModel: {
    groupId: 'address',
    formFields: {
      street1: {
        name: 'street1',
        label: 'Address',
        placeholder: 'Search address',
        min: 3,
        max: 50,
        matches: REG_EX.lettersNumbersSpacesPeriodsHyphensSlashesHashes,
        requiredErrMsg: 'Address is a required field.',
        minErrMsg: 'Too short. Address must be at least 3 characters long.',
        maxErrMsg: 'Too long. Address must be 50 characters or less.',
        matchesErrMsg:
          'Address field can only contain letters, numbers, spaces, hyphens, periods, hashes, and slashes.',
      },
      street2: {
        name: 'street2',
        label: 'Apartment/Suite',
        placeholder: 'Enter apartment/suite',
        min: 3,
        max: 50,
        matches: REG_EX.lettersNumbersSpacesHyphensSlashesHashesPeriods,
        minErrMsg:
          'Too short. Your apartment/suite must be at least 3 characters long (e.g. use APT 22 instead of 22).',
        maxErrMsg: 'Too long. Apartment/suite must be 50 characters or less.',
        matchesErrMsg:
          'Apartment/suite can only contain letters, numbers, spaces, periods, hyphens, hashes and slashes.',
      },
      city: {
        name: 'city',
        label: 'City',
        placeholder: 'Enter city',
        min: 2,
        max: 40,
        matches: REG_EX.lettersSpacesHyphen,
        requiredErrMsg: 'City is a required field.',
        minErrMsg: 'City must be at least 2 characters long.',
        maxErrMsg: 'City must be 40 characters or less.',
        matchesErrMsg: 'City can only contain letters, spaces, and hyphens.',
        nonMatchesErrMsg: 'City cannot begin or end with a hyphen.',
      },
      state: {
        name: 'state',
        label: 'State',
        placeholder: 'Select state',
        requiredErrMsg: 'You must select a state from the drop-down menu.',
        oneOfErrMsg: 'You must select a state from the drop-down menu.',
      },
      zip: {
        name: 'zip',
        label: 'Zip code',
        placeholder: 'Enter zip code',
        length: 5,
        // mask: GENERAL.zipMask,
        matches: REG_EX.numbers,
        requiredErrMsg: 'Zip code is a required field.',
        lengthErrMsg: 'Zip code must be five digits.',
        matchesErrMsg: 'Zip code can only contain numbers.',
      },
      country: {
        name: 'country',
        label: 'Country',
        placeholder: 'Select country',
        requiredErrMsg: 'Country is required.',
      },
    },
  },
  birthDateModel: {
    formFields: {
      birthDate: {
        name: 'birthDate',
        format: {
          format: 'MM/DD/YYYY',
          type: 'mask' as const,
        },
        label: 'Date of birth',
        placeholder: 'MM/DD/YYYY',
        minAge: 18,
        maxAge: 120,
        maskedValue: '••/••/••••',
        requiredErrMsg: 'Date of birth is required.',
      },
    },
  },
  phoneNumberModel: {
    formFields: {
      phoneNumber: {
        name: 'phoneNumber',
        label: 'Phone number',
        placeholder: 'Enter phone number',
        length: 10,
        mask: GENERAL.phoneNumberMask,
        matches: REG_EX.phoneNumber,
        requiredErrMsg: 'Phone number is a required field.',
        lengthErrMsg: 'Phone number must be 10 digits in length.',
        matchesErrMsg:
          'Phone number is invalid. Please provide a valid phone number.',
      },
    },
  },
  ssnModel: {
    formFields: {
      ssn: {
        name: 'ssn',
        label: 'Social Security Number',
        placeholder: 'Social Security Number',
        length: 9,
        matches: REG_EX.ssn,
        requiredErrMsg: 'Social Security Number is required.',
        lengthErrMsg: 'Social Security Number must be 9 digits.',
        matchesErrMsg: 'The entered SSN is not valid.',
      },
      confirmSsn: {
        name: 'confirmSsn',
        label: 'Confirm Social Security Number',
        placeholder: 'Confirm Social Security Number',
        requiredErrMsg: 'Confirm Social Security Number is required.',
        matchesErrMsg: 'Social Security Number must match.',
      },
    },
  },
  emailAddressModel: {
    formFields: {
      emailAddress: {
        name: 'emailAddress',
        type: 'text',
        label: 'Email address',
        placeholder: 'Enter email address',
        max: 100,
        matches: REG_EX.email,
        requiredErrMsg: 'Email address is required.',
        emailErrMsg: 'Email address must be in the format xxx@xxx.xxx.',
        maxErrMsg: 'Email address must be less than 100 characters',
        matchesErrMsg: 'You must enter a valid email address.',
      },
    },
  },
};

export default piiFormModel;
